import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { formatTime } from "/imports/ui/views/TrainerDashboardView/trainerScheduleHelpers";
import { accentFor } from "/imports/ui/views/TrainerDashboardView/attendanceHelpers";
import { HOUR_REM, MIN_BLOCK_REM, rem } from "/imports/ui/components/scheduleGrid/gridHelpers";
import { servicePalette } from "../fullScheduleHelpers";

// A block only reveals a detail line once it is tall enough to hold it — same
// rem thresholds as the dashboard's WeekBookingBlock.
const SHOW_NAME_REM = 1.875;
const SHOW_CONTEXT_REM = 3.5;

// One booking block in the full-schedule grid. The accent color arrives
// pre-computed from the API (service → category → default, BLOCKING grey);
// PENDING holds render dimmed. Every block opens the detail sheet.
function FullScheduleBlock({ item, gridStartMin, laneCount, onOpen, contextFor }) {
  const b = item.booking;
  const topRem = ((item.start - gridStartMin) / 60) * HOUR_REM;
  const heightRem = Math.max(((item.end - item.start) / 60) * HOUR_REM, MIN_BLOCK_REM);
  const widthPct = 100 / laneCount;
  const timeLabel = [formatTime(item.start), formatTime(item.end)].filter(Boolean).join(" – ");
  const name = b.type === "BLOCKING" ? b.title || "Blocked" : b.serviceName || b.title || "Booking";
  const context = contextFor ? contextFor(b) : "";
  const seats = parseInt(b.maxSeats, 10) > 1 ? `${b.activeSeats}/${b.maxSeats}` : "";
  const palette = servicePalette(b.color);
  // Same signal as the trainer's own week view: red once anyone is a no-show/LMC, green when all attended.
  const attendanceMark = accentFor(b.clients);

  return (
    <Box
      onClick={onOpen ? () => onOpen(b) : undefined}
      sx={{
        position: "absolute",
        top: rem(topRem),
        height: rem(heightRem),
        left: `calc(${item.lane * widthPct}% + 2px)`,
        width: `calc(${widthPct}% - 4px)`,
        bgcolor: palette.fill,
        color: "text.primary",
        borderLeft: 3,
        borderColor: palette.edge,
        borderRadius: 0.75,
        px: 0.75,
        py: 0.25,
        overflow: "hidden",
        fontSize: "0.6875rem",
        lineHeight: 1.25,
        cursor: onOpen ? "pointer" : "default",
        opacity: b.type === "PENDING" ? 0.55 : 1,
        transition: "background-color 120ms",
        "&:hover": onOpen ? { bgcolor: palette.hoverFill } : undefined
      }}
    >
      {attendanceMark && (
        <Box
          sx={{
            position: "absolute",
            top: "0.3125rem",
            right: "0.3125rem",
            width: "0.5rem",
            height: "0.5rem",
            borderRadius: "50%",
            bgcolor: attendanceMark,
            boxShadow: (theme) => `0 0 0 0.125rem ${theme.palette.background.paper}`
          }}
        />
      )}
      <Typography noWrap sx={{ fontSize: "0.6875rem", fontWeight: 700, color: palette.ink, pr: attendanceMark ? 1 : 0 }}>
        {timeLabel}
        {seats ? ` · ${seats}` : ""}
      </Typography>
      {heightRem >= SHOW_NAME_REM && (
        <Typography noWrap sx={{ fontSize: "0.6875rem", fontWeight: 500 }}>
          {name}
        </Typography>
      )}
      {heightRem >= SHOW_CONTEXT_REM && context && (
        <Typography noWrap sx={{ fontSize: "0.625rem", color: "text.secondary" }}>
          {context}
        </Typography>
      )}
    </Box>
  );
}

export default FullScheduleBlock;
